/* ==========================================================================
   HELENA — shared behaviour
   Scroll reveal, word-split headings, soft parallax, count-up numbers,
   and a small horizontal carousel helper used by several sections.
   No dependencies. Safe to load once, deferred.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = document.documentElement.classList.contains('shopify-design-mode');

  /* ---------- Word splitting for animated headings ---------- */
  function splitWords(el) {
    if (el.dataset.lySplit === 'done') return;
    var nodes = Array.prototype.slice.call(el.childNodes);
    var frag = document.createDocumentFragment();
    var index = 0;
    nodes.forEach(function (n) {
      if (n.nodeType === 3) {
        n.textContent.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
          frag.appendChild(wrapWord(part, index++));
        });
      } else if (n.nodeType === 1) {
        var clone = n.cloneNode(false);
        var text = n.textContent;
        text.split(/(\s+)/).forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) { clone.appendChild(document.createTextNode(' ')); return; }
          clone.appendChild(wrapWord(part, index++));
        });
        frag.appendChild(clone);
      }
    });
    el.innerHTML = '';
    el.appendChild(frag);
    el.dataset.lySplit = 'done';
  }
  function wrapWord(text, i) {
    var w = document.createElement('span');
    w.className = 'ly-word';
    var inner = document.createElement('span');
    inner.textContent = text;
    inner.style.setProperty('--ly-wd', (i * 55) + 'ms');
    w.appendChild(inner);
    return w;
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal(scope) {
    scope = scope || document;
    var targets = scope.querySelectorAll('[data-ly-reveal], [data-ly-stagger], .ly-words');
    if (!targets.length) return;

    targets.forEach(function (t) {
      if (t.classList.contains('ly-words')) splitWords(t);
      if (t.hasAttribute('data-ly-stagger')) {
        var step = parseInt(t.getAttribute('data-ly-stagger'), 10) || 90;
        Array.prototype.forEach.call(t.children, function (c, i) {
          c.style.setProperty('--ly-d', (i * step) + 'ms');
        });
      }
    });

    if (reduce || designMode || !('IntersectionObserver' in window)) {
      targets.forEach(function (t) { t.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    targets.forEach(function (t) { io.observe(t); });
  }

  /* ---------- Soft parallax (translateY based on viewport progress) ---------- */
  function initParallax() {
    var els = document.querySelectorAll('[data-ly-parallax]');
    if (!els.length || reduce) return;
    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var amount = parseFloat(el.getAttribute('data-ly-parallax')) || 12; // percentage travel
        var r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        var progress = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2); // -1..1
        el.style.transform = 'translate3d(0,' + (progress * amount * -1) + '%,0) scale(1.08)';
      });
      ticking = false;
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  }

  /* ---------- Count-up numbers: data-ly-count="98" data-ly-suffix="%" ---------- */
  function initCountUp() {
    var els = document.querySelectorAll('[data-ly-count]');
    if (!els.length) return;
    function run(el) {
      var target = parseFloat(el.getAttribute('data-ly-count'));
      var decimals = (el.getAttribute('data-ly-count').split('.')[1] || '').length;
      var suffix = el.getAttribute('data-ly-suffix') || '';
      var prefix = el.getAttribute('data-ly-prefix') || '';
      var dur = 1400, start = null;
      if (reduce || designMode) { el.textContent = prefix + target.toFixed(decimals) + suffix; return; }
      function frame(ts) {
        if (!start) start = ts;
        var p = Math.min(1, (ts - start) / dur);
        var eased = 1 - Math.pow(1 - p, 4);
        el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
        if (p < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }
    if (!('IntersectionObserver' in window)) { els.forEach(run); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Horizontal snap carousel helper ----------
     Usage: window.HelenaCarousel(track, { prev, next, dots, gap })
     Track is a scroll-snap container. Returns { update }.
  ------------------------------------------------------ */
  function Carousel(track, opts) {
    opts = opts || {};
    var items = track.children;
    var prev = opts.prev, next = opts.next, dotsWrap = opts.dots, nav = opts.nav;
    if (!items.length) { if (nav) nav.style.display = 'none'; return null; }

    function gap() { return parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0; }
    function step() { return items[0].getBoundingClientRect().width + gap(); }
    function maxScroll() { return track.scrollWidth - track.clientWidth; }
    function fits() { return maxScroll() <= 2; }
    function pageCount() { return Math.max(1, Math.round(maxScroll() / step()) + 1); }
    function current() { return Math.min(pageCount() - 1, Math.round(track.scrollLeft / step())); }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      for (var i = 0; i < pageCount(); i++) {
        var d = document.createElement('button');
        d.type = 'button';
        d.className = 'ly-dot';
        d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        d.addEventListener('click', (function (idx) { return function () { track.scrollTo({ left: idx * step(), behavior: 'smooth' }); }; })(i));
        dotsWrap.appendChild(d);
      }
    }
    function update() {
      var f = fits();
      if (nav) nav.classList.toggle('is-hidden', f);
      track.classList.toggle('is-fits', f);
      if (f) return;
      if (dotsWrap && dotsWrap.children.length !== pageCount()) buildDots();
      var cur = current();
      if (dotsWrap) Array.prototype.forEach.call(dotsWrap.children, function (d, i) { d.classList.toggle('active', i === cur); });
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll() - 2;
    }
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
    var t;
    track.addEventListener('scroll', function () { clearTimeout(t); t = setTimeout(update, 60); }, { passive: true });
    window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(update, 120); });

    /* Drag to scroll on desktop */
    var down = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      down = true; moved = false; startX = e.clientX; startLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!down) return;
      down = false; track.classList.remove('is-dragging');
      if (moved) { var snap = Math.round(track.scrollLeft / step()) * step(); track.scrollTo({ left: snap, behavior: 'smooth' }); }
    });
    track.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);

    update();
    return { update: update };
  }

  window.HelenaCarousel = Carousel;
  window.HelenaReveal = initReveal;

  function boot() {
    initReveal(document);
    initParallax();
    initCountUp();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  /* Theme editor: re-run when sections are (re)loaded */
  document.addEventListener('shopify:section:load', function (e) { initReveal(e.target); initCountUp(); });
})();
